import axios from 'axios'
import Axhelper from '@/lib/Axhelper'

class RozmowaService {
	constructor() {
		this.baseUrl = window.RUNTIME_CONFIG.API_URL
		this.endpoints = {
			rozmowaAll: '/api/rozmowa/filters',
			rozmowaDetails: '/api/rozmowa/details',
			rozmowaSearch: '/api/rozmowa/filters',
			rozmowaCreate: '/api/rozmowa',
			rozmowaUpdate: '/api/rozmowa',
			rozmowaDelete: '/api/rozmowa',
			rozmowaDial: '/api/rozmowa/dial',
			rozmowaDownloadRecording: '/api/rozmowa/downloadrecording',
			rozmowaOpportunitiesCount: '/api/rozmowa/getopportunitiescount',
		}
	}

	appendFilters(params, filters = {}) {
		const allowedFilters = [
			'id_user',
			'id_placowka',
			'id_osoba',
			'id_miasto',
			'id_gmina',
			'id_powiat',
			'id_wojewodztwo',
			'id_typ',
			'priorytet',
			'call_time',
			'skipped',
			'szansa',
			'created',
			'dig_from_date',
			'dig_to_date',
			'dig_nastepnej_rozmowy_from_date',
			'dig_nastepnej_rozmowy_to_date',
		]

		allowedFilters.forEach(filterKey => {
			const value = filters?.[filterKey]

			if (value !== undefined && value !== null && value !== '') {
				params.append(filterKey, String(value))
			}
		})
	}

	async getAll(options = {}) {
		try {
			const {
				limit = 50,
				sortColumn = 'dig_rozmowy',
				sortDirection = 'desc',
				filters = {},
			} = options

			const params = new URLSearchParams({
				sortcol: sortColumn,
				sortdir: sortDirection,
				limit: String(limit),
			})
			this.appendFilters(params, filters)
			const url = `${this.endpoints.rozmowaAll}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: response.data?.length || 0

			return {
				data: response.data || [],
				error: null,
				totalCount,
				hasMore: (response.data?.length || 0) < totalCount,
				headers: response.headers,
			}
		} catch (error) {
			console.error('RozmowaService.getAll error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch rozmowy',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async getById(rozmowaId) {
		try {
			if (!rozmowaId) {
				return {
					data: null,
					error: 'Invalid rozmowa ID provided',
				}
			}

			const url = `${this.endpoints.rozmowaDetails}/${encodeURIComponent(rozmowaId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: null,
					error: response.error,
				}
			}

			return {
				data: response.data || null,
				error: null,
			}
		} catch (error) {
			console.error('RozmowaService.getById error:', error)
			return {
				data: null,
				error: error.message || 'Failed to fetch rozmowa details',
			}
		}
	}

	async getByPlacowkaId(placowkaId) {
		try {
			if (!placowkaId) {
				return {
					data: [],
					error: 'Invalid placowka ID provided',
				}
			}

			const url = `${this.endpoints.rozmowaSearch}?id_placowka=${encodeURIComponent(placowkaId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
				}
			}

			return {
				data: response.data || [],
				error: null,
			}
		} catch (error) {
			console.error('RozmowaService.getByPlacowkaId error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch rozmowa by placowka ID',
			}
		}
	}

	async getByOsobaId(osobaId) {
		try {
			if (!osobaId) {
				return {
					data: [],
					error: 'Invalid osoba ID provided',
				}
			}

			const url = `${this.endpoints.rozmowaSearch}?id_osoba=${encodeURIComponent(osobaId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
				}
			}

			return {
				data: response.data || [],
				error: null,
			}
		} catch (error) {
			console.error('RozmowaService.getByOsobaId error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch rozmowa by osoba ID',
			}
		}
	}

	async search(searchTerm, options = {}) {
		try {
			if (!searchTerm) {
				return {
					data: [],
					error: 'Invalid search term provided',
					totalCount: 0,
					hasMore: false,
				}
			}

			const {
				limit = 50,
				sortColumn = 'dig_rozmowy',
				sortDirection = 'desc',
				filters = {},
			} = options

			const params = new URLSearchParams({
				sortcol: sortColumn,
				sortdir: sortDirection,
				limit: String(limit),
			})
			params.append('searchText', searchTerm)
			this.appendFilters(params, filters)
			const url = `${this.endpoints.rozmowaSearch}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: response.data?.length || 0

			return {
				data: response.data || [],
				error: null,
				totalCount,
				hasMore: (response.data?.length || 0) < totalCount,
				headers: response.headers,
			}
		} catch (error) {
			console.error('RozmowaService.search error:', error)
			return {
				data: [],
				error: error.message || 'Failed to search rozmowy',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async create(rozmowaData) {
		try {
			const response = await Axhelper.sendPostAsync(this.endpoints.rozmowaCreate, rozmowaData)

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('RozmowaService.create error:', error)
			return {
				success: false,
				error: error.message || 'Failed to create rozmowa',
			}
		}
	}

	async update(rozmowaId, rozmowaData) {
		try {
			if (!rozmowaId) {
				return {
					success: false,
					error: 'Invalid rozmowa ID provided',
				}
			}

			const url = `${this.endpoints.rozmowaUpdate}/${encodeURIComponent(rozmowaId)}`
			const response = await Axhelper.sendPutAsync(url, rozmowaData)

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('RozmowaService.update error:', error)
			return {
				success: false,
				error: error.message || 'Failed to update rozmowa',
			}
		}
	}

	async getOpportunitiesCount() {
		try {
			const response = await Axhelper.sendGetAsync(this.endpoints.rozmowaOpportunitiesCount)

			if (response.error) {
				return {
					count: 0,
					error: response.error,
				}
			}

			const raw = response.data
			const count =
				typeof raw === 'number'
					? raw
					: typeof raw?.count === 'number'
						? raw.count
						: parseInt(raw, 10) || 0

			return {
				count,
				error: null,
			}
		} catch (error) {
			console.error('RozmowaService.getOpportunitiesCount error:', error)
			return {
				count: 0,
				error: error.message || 'Failed to fetch opportunities count',
			}
		}
	}

	async downloadRecording(rozmowaId) {
		try {
			if (!rozmowaId) {
				return {
					success: false,
					error: 'Invalid rozmowa ID provided',
					blob: null,
					filename: null,
				}
			}

			const url = `${this.endpoints.rozmowaDownloadRecording}/${encodeURIComponent(rozmowaId)}`
			const response = await this._downloadBlob(url)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					blob: null,
					filename: null,
				}
			}

			const filename = this._extractDownloadFilename(response.headers, rozmowaId)

			return {
				success: true,
				error: null,
				blob: response.blob,
				filename,
			}
		} catch (error) {
			console.error('RozmowaService.downloadRecording error:', error)
			return {
				success: false,
				error: error.message || 'Failed to download recording',
				blob: null,
				filename: null,
			}
		}
	}

	async _downloadBlob(url) {
		try {
			const token = localStorage.getItem('token')
			const response = await axios.get(url, {
				responseType: 'blob',
				withCredentials: true,
				headers: { token },
			})

			return { error: null, blob: response.data, headers: response.headers }
		} catch (error) {
			const errorMessage = error.response?.data || error.message || 'Download failed'
			return { error: errorMessage, blob: null, headers: null }
		}
	}

	_extractDownloadFilename(headers, rozmowaId) {
		const defaultFilename = `recording-${rozmowaId}`

		if (!headers) return defaultFilename

		const disposition = headers['content-disposition'] || ''
		const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
		if (utf8Match?.[1]) {
			return decodeURIComponent(utf8Match[1])
		}

		const regularMatch = disposition.match(/filename="?([^";]+)"?/i)
		if (regularMatch?.[1]) {
			return regularMatch[1]
		}

		return defaultFilename
	}

	async dial(idRozmowa, targetPhone) {
		try {
			if (!idRozmowa) {
				return {
					success: false,
					error: 'Invalid rozmowa ID provided',
				}
			}

			if (!targetPhone) {
				return {
					success: false,
					error: 'Invalid target phone provided',
				}
			}

			const payload = new URLSearchParams()
			payload.append('id_rozmowa', String(idRozmowa))
			payload.append('target_phone', String(targetPhone))

			const response = await Axhelper.sendPostAsync(this.endpoints.rozmowaDial, payload)

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('RozmowaService.dial error:', error)
			return {
				success: false,
				error: error.message || 'Failed to dial rozmowa',
			}
		}
	}

	async delete(rozmowaId) {
		try {
			if (!rozmowaId) {
				return {
					success: false,
					error: 'Invalid rozmowa ID provided',
					message: null,
				}
			}

			const url = `${this.endpoints.rozmowaDelete}/${encodeURIComponent(rozmowaId)}`
			const response = await Axhelper.sendDeleteAsync(url)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					message: null,
				}
			}

			return {
				success: true,
				error: null,
				message: response.data || 'Rozmowa deleted successfully',
			}
		} catch (error) {
			console.error('RozmowaService.delete error:', error)
			return {
				success: false,
				error: error.message || 'Failed to delete rozmowa',
				message: null,
			}
		}
	}
}

export default new RozmowaService()
